import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function Pricing() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const { t } = useLang();
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(false);

  const FREE = [t("pricing.free1"), t("pricing.free2"), t("pricing.free3"), t("pricing.free4")];
  const PRO = [t("pricing.pro1"), t("pricing.pro2"), t("pricing.pro3"), t("pricing.pro4")];

  const subscribe = async () => {
    if (!user) { sessionStorage.setItem("decide_post_login", "/pricing"); login(); return; }
    setBusy(true);
    try {
      const lookup_key = yearly ? "decide_pro_yearly" : "decide_pro_monthly";
      const res = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(t("toast.checkoutError"));
      setBusy(false);
    }
  };

  return (
    <AppShell showNav={!!user}>
      <button data-testid="pricing-back" onClick={() => navigate(-1)} className="btn-ghost !px-0 mb-2"><ArrowLeft size={18} /> {t("pricing.back")}</button>

      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-decide-ink">{t("pricing.title")}</h1>
        <p className="mt-3 text-decide-slate">{t("pricing.sub")}</p>
      </div>

      {user?.is_pro && (
        <div className="mt-6 rounded-2xl bg-decide-forestLight p-4 text-center text-decide-forest font-semibold" data-testid="already-pro">{t("pricing.alreadyPro")}</div>
      )}

      <div className="mt-7 flex items-center justify-center">
        <div className="inline-flex rounded-full border border-decide-line bg-decide-surface p-1" data-testid="billing-toggle">
          <button data-testid="toggle-monthly" onClick={() => setYearly(false)} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!yearly ? "bg-decide-forest text-white" : "text-decide-slate"}`}>{t("pricing.monthly")}</button>
          <button data-testid="toggle-yearly" onClick={() => setYearly(true)} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${yearly ? "bg-decide-forest text-white" : "text-decide-slate"}`}>{t("pricing.yearly")} <span className={yearly ? "text-white/80" : "text-decide-forest"}>–37%</span></button>
        </div>
      </div>

      <div className="mt-6 decide-card">
        <p className="decide-label">{t("pricing.free")}</p>
        <p className="mt-2 text-3xl font-light text-decide-ink">€0</p>
        <ul className="mt-4 space-y-2.5">
          {FREE.map((f) => (<li key={f} className="flex items-center gap-3 text-[15px] text-decide-ink"><Check size={16} className="text-decide-slate" /> {f}</li>))}
        </ul>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-2xl bg-decide-panel p-6 text-white" data-testid="pro-plan-card">
        <div className="flex items-center gap-2 text-decide-panelaccent">
          <Sparkles size={15} /><span className="text-[11px] font-bold uppercase tracking-[0.18em]">DECIDE Pro</span>
        </div>
        <div className="mt-3 flex items-end gap-1.5">
          <span className="text-4xl font-semibold" data-testid="pro-price">{yearly ? "€29.99" : "€3.99"}</span>
          <span className="mb-1 text-white/60">{yearly ? t("pricing.perYear") : t("pricing.perMonth")}</span>
        </div>
        {yearly && <p className="mt-1 text-[13px] text-decide-panelaccent">{t("pricing.yearlyNote")}</p>}
        <ul className="mt-5 space-y-2.5">
          {PRO.map((f) => (
            <li key={f} className="flex items-center gap-3 text-[15px] text-white/90">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-decide-forest"><Check size={12} strokeWidth={3} /></span> {f}
            </li>
          ))}
        </ul>
        <button data-testid="subscribe-btn" disabled={busy || user?.is_pro} onClick={subscribe} className="mt-6 w-full rounded-full bg-decide-surface text-decide-ink dark:bg-decide-forest dark:text-decide-canvas py-3.5 font-semibold transition-transform active:scale-[0.98] disabled:opacity-50">
          {user?.is_pro ? t("pricing.onPro") : busy ? t("pricing.starting") : user ? t("pricing.upgradeBtn") : t("pricing.signinUpgrade")}
        </button>
      </motion.div>

      <p className="mt-4 text-center text-[12px] text-decide-mute">{t("pricing.fine")}</p>
    </AppShell>
  );
}
